import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { useMemo } from 'react';
import { RELEWISE_MINIMUM_STOCK_COUNT } from '../../integrations/relewise/const';
import { useMappingService } from '../../integrations/relewise/hooks/serviceHooks';
import { ProductColor, VariantDetails } from '../../integrations/relewise/types/mapped';
import { useVariantsAvailability } from './useVariantsAvailability';

interface UseAvailabilityByColorAndSizeArgs {
    context: ICoreContext;
    variantsByKey: Record<string, VariantDetails> | undefined;
    allColors: ProductColor[] | undefined;
    selectedVariant: VariantDetails | undefined;
    selectedColor: ProductColor | undefined;
    selectedSize: string | undefined;
    compatibleSizesByColor: Record<string, string[]> | undefined;
    productItemId: string | undefined;
    moduleName: string;
}
export const useAvailabilityByColorAndSize = ({
    context,
    allColors,
    variantsByKey,
    selectedVariant,
    selectedColor,
    selectedSize,
    compatibleSizesByColor,
    productItemId,
    moduleName
}: UseAvailabilityByColorAndSizeArgs) => {
    const variantRecordIds = useMemo(() => {
        return Object.values(variantsByKey ?? {}).map(variant => variant.variantRecordId);
    }, [variantsByKey]);

    const { availability: availabilityByVariantRecordId, isLoading } = useVariantsAvailability(
        context.actionContext,
        variantRecordIds,
        moduleName
    );
    const relewiseMappingService = useMappingService(context.request, context.telemetry);

    const availabilityByColor = useMemo(() => {
        if (!variantsByKey || !allColors || isLoading || typeof window === 'undefined') {
            return;
        }

        const availability: Record<string, boolean> = {};

        // Check for each color that there is at least one available variant
        // Variant is considered available if above stock threshold and in assortment
        allColors.forEach(color => {
            const hasAvailableVariant = Object.values(variantsByKey).some(variant => {
                const variantColor = variant.color.name;
                const variantAvailability = availabilityByVariantRecordId?.get(variant.variantRecordId) ?? variant.fallbackStockCount ?? 0;
                const isVariantInAssortment = variant.isInAssortment;
                return variantColor === color.name && variantAvailability > RELEWISE_MINIMUM_STOCK_COUNT && isVariantInAssortment;
            });
            availability[color.name] = hasAvailableVariant;
        });

        return availability;
    }, [variantsByKey, availabilityByVariantRecordId, allColors, isLoading]);

    const availabilityForSelectedColorBySize = useMemo(() => {
        if (!selectedColor || !variantsByKey || !compatibleSizesByColor || !productItemId || isLoading || typeof window === 'undefined') {
            return;
        }

        const availability: Record<string, boolean> = {};
        const compatibleSizes = compatibleSizesByColor[selectedColor.name] ?? [];

        compatibleSizes.forEach(size => {
            const variantKey = relewiseMappingService.mapProductDimensionsToRelewiseVariantId(productItemId, selectedColor.name, size);
            const currentVariant = variantsByKey[variantKey];
            const variantRecordId = currentVariant?.variantRecordId;
            if (!variantRecordId) {
                availability[size] = false;
                return;
            }
            const variantAvailability = availabilityByVariantRecordId?.get(variantRecordId) ?? currentVariant.fallbackStockCount ?? 0;
            const isVariantInAssortment = currentVariant?.isInAssortment ?? false;
            const hasAvailableVariant = variantAvailability > RELEWISE_MINIMUM_STOCK_COUNT && isVariantInAssortment;
            availability[size] = hasAvailableVariant;
        });

        return availability;
    }, [
        selectedColor,
        variantsByKey,
        compatibleSizesByColor,
        productItemId,
        isLoading,
        relewiseMappingService,
        availabilityByVariantRecordId
    ]);

    const selectedColorHasStock = selectedColor && !isLoading ? !!availabilityByColor?.[selectedColor.name] : true;
    const selectedVariantAvailability =
        selectedVariant && selectedSize ? availabilityByVariantRecordId?.get(selectedVariant.variantRecordId) : undefined;
    const selectedVariantInAssortment = selectedVariant?.isInAssortment;
    const currentSelectionOutOfStock =
        selectedVariantAvailability !== undefined
            ? selectedVariantAvailability <= RELEWISE_MINIMUM_STOCK_COUNT || !selectedVariantInAssortment
            : !selectedColorHasStock;

    return { availabilityByColor, currentSelectionOutOfStock, availabilityForSelectedColorBySize, isLoading };
};
